/**
 * SettingsLeadDisposition Component - Full API Integration
 * 
 * This component manages the Lead Disposition settings page with full backend integration:
 * - View all lead dispositions in a table
 * - Add new lead dispositions via modal with API call
 * - Edit existing dispositions with API updates
 * - Delete dispositions with confirmation
 * - Real-time status updates
 */

import React, { useState, useEffect } from 'react';
import editIcon from '../assets/Edit (1).svg';
import leadDispositionIcon from '../assets/Lead Disposition.svg';
import { leadDispositionApi, authHelpers } from '../services/api'; // Adjust path as needed
import { useTheme } from '../context/ThemeContext';
import { X, Trash2 } from 'lucide-react';
import { message } from 'antd';

/**
 * Toggle Component
 */
function Toggle({ checked, onChange, ariaLabel }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 border-2`}
      style={{
        backgroundColor: checked ? currentTheme.color : '#FFFFFF',
        borderColor: isDark ? '#FFFFFF' : '#D1D5DB',
        '--tw-ring-color': currentTheme.color
      }}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full transition-transform ${checked ? 'translate-x-5 bg-white' : 'translate-x-1 bg-gray-400'
          } border-2 border-white shadow-sm`}
      />
    </button>
  );
}

/**
 * Delete Icon Component
 */
function DeleteIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Formats a date string to "DD/MM/YYYY, HH:MM AM/PM" format
 */
function formatDateTime(dateInput) {
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '-';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, '0');

  return `${day}/${month}/${year}, ${formattedHours}:${minutes} ${ampm}`;
}

/**
 * Main Component
 */
export default function SettingsLeadDisposition() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  // State management
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [addForm, setAddForm] = useState({
    dispositionName: '',
    status: true,
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const dispositionsData = await leadDispositionApi.getAllDispositions();

      // Transform API response to match UI format
      const transformedRows = dispositionsData.map((item) => ({
        id: item.id.toString(),
        dispositionName: item.dispositionName,
        status: item.status,
        createdBy: item.createdByName || 'Unknown',
        createdAt: item.createdAt,
      }));

      setRows(transformedRows);
    } catch (err) {
      console.error('Error fetching dispositions:', err);
      setError(err.message || err.error || 'Failed to load dispositions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding new disposition
   */
  const handleAddSubmit = async () => {
    try {
      // Validate form
      if (!addForm.dispositionName.trim()) {
        message.warning('Please enter a disposition name');
        return;
      }

      // Get current user ID from localStorage
      const currentUser = authHelpers.getCurrentUser();
      const createdById = currentUser?.id || 1;

      // Prepare data for API
      const apiData = {
        dispositionName: addForm.dispositionName.trim(),
        status: addForm.status,
        createdById: createdById,
      };

      // Call API
      await leadDispositionApi.createDisposition(apiData);

      // Refresh data
      await fetchData();

      // Close modal and reset form
      setIsAddOpen(false);
      setAddForm({
        dispositionName: '',
        status: true,
      });

      message.success('Disposition created successfully!');
    } catch (err) {
      console.error('Error creating disposition:', err);
      message.error(err.message || err.error || 'Failed to create disposition. Please try again.');
    }
  };

  /**
   * Handle editing disposition
   */
  const handleEditSubmit = async () => {
    try {
      if (!editingRow) return;

      // Validate form
      if (!editingRow.dispositionName.trim()) {
        message.warning('Please enter a disposition name');
        return;
      }

      // Prepare data for API
      const apiData = {
        dispositionName: editingRow.dispositionName.trim(),
        status: editingRow.status,
      };

      // Call API
      await leadDispositionApi.updateDisposition(editingRow.id, apiData);

      // Refresh data
      await fetchData();

      // Close modal
      setIsEditOpen(false);
      setEditingRow(null);

      message.success('Disposition updated successfully!');
    } catch (err) {
      console.error('Error updating disposition:', err);
      message.error(err.message || err.error || 'Failed to update disposition. Please try again.');
    }
  };

  /**
   * Handle status toggle (inline update)
   */
  const handleStatusToggle = async (rowId) => {
    try {
      const row = rows.find(r => r.id === rowId);
      if (!row) return;

      // Update local state immediately for better UX
      setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, status: !r.status } : r)));

      // Call API to toggle status
      await leadDispositionApi.toggleDispositionStatus(rowId);
    } catch (err) {
      console.error('Error toggling status:', err);
      // Revert local state on error
      await fetchData();
      message.error('Failed to update status. Please try again.');
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async (rowId, dispositionName) => {
    if (!window.confirm(`Are you sure you want to delete disposition "${dispositionName}"?`)) {
      return;
    }

    try {
      await leadDispositionApi.deleteDisposition(rowId);
      await fetchData();
      message.success('Disposition deleted successfully!');
    } catch (err) {
      console.error('Error deleting disposition:', err);
      message.error(err.message || err.error || 'Failed to delete disposition. Please try again.');
    }
  };

  /**
   * Open edit modal
   */
  const openEditModal = (row) => {
    setEditingRow({ ...row });
    setIsEditOpen(true);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dispositions...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={fetchData}
          className="px-6 py-2 rounded-lg text-white transition-colors"
          style={{ backgroundColor: currentTheme.color }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 relative min-h-screen -mx-8 px-8 pb-8 transition-colors duration-300 ${isDark ? 'bg-[#0f172a] text-white' : ''}`}>
      {/* Page Header Section */}
      <div className="sticky top-0 z-30 -mx-8 px-8 py-3" style={{ backgroundColor: `${currentTheme.color}10` }}>
        <div className="flex items-center justify-between">
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Lead Disposition</h2>

          {/* Add New Disposition Button */}
          <button
            type="button"
            onClick={() => {
              setAddForm({
                dispositionName: '',
                status: true,
              });
              setIsAddOpen(true);
            }}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition-colors"
            style={{ backgroundColor: currentTheme.color }}
            title="Add Lead Disposition"
            aria-label="Add Lead Disposition"
          >
            <img
              src={leadDispositionIcon}
              alt="Add Lead Disposition"
              className="h-10 w-10"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>

      {/* Add New Disposition Modal */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Add New Lead Disposition"
          onClick={() => setIsAddOpen(false)}
        >
          <div
            className={`w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: `${currentTheme.color}15` }}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Add New Lead Disposition</h3>
              <button
                type="button"
                onClick={() => setIsAddOpen(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ color: currentTheme.color }}
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Disposition Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={addForm.dispositionName}
                    onChange={(e) => setAddForm((p) => ({ ...p, dispositionName: e.target.value }))}
                    placeholder="Enter disposition name"
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'border-gray-300 text-gray-900'}`}
                    style={{ focusBorderColor: currentTheme.color }}
                    required
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                  <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Disposition Modal */}
      {isEditOpen && editingRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Edit Lead Disposition"
          onClick={() => setIsEditOpen(false)}
        >
          <div
            className={`w-full max-w-xl overflow-hidden rounded-2xl shadow-2xl border ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-white/50'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-6 py-4 ${isDark ? 'bg-white/5 border-b border-white/10' : 'bg-teal-100'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Edit Lead Disposition</h3>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="p-2 rounded-lg hover:bg-teal-200/60 transition-colors"
                aria-label="Close"
                title="Close"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-gray-700" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className={`block text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                    Disposition Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={editingRow.dispositionName}
                    onChange={(e) => setEditingRow((p) => ({ ...p, dispositionName: e.target.value }))}
                    placeholder="Enter disposition name"
                    className={`w-full h-10 px-3 border rounded-lg focus:outline-none transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'border-gray-300 text-gray-900'}`}
                    style={{ focusBorderColor: currentTheme.color }}
                    required
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                  <Toggle
                    checked={editingRow.status}
                    onChange={(v) => setEditingRow((p) => ({ ...p, status: v }))}
                    ariaLabel="Toggle status"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                  <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  className={`px-6 py-2.5 rounded-xl border font-semibold transition-colors ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  className="px-6 py-2.5 rounded-xl text-white font-semibold transition-colors"
                  style={{ backgroundColor: currentTheme.color }}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Data Table Section */}
      <div className={`${isDark ? 'bg-[#1e293b]/80 border-white/10' : 'bg-white/80 border-white/50'} backdrop-blur rounded-2xl shadow-xl border overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="min-w-[800px] w-full">
            <thead>
              <tr className="text-white" style={{ backgroundColor: currentTheme.color }}>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Disposition Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Created At
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-gray-200'}`}>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 === 0 ? (isDark ? 'bg-[#1e293b]' : 'bg-white') : ''} style={{ backgroundColor: idx % 2 === 0 ? undefined : `${currentTheme.color}08` }}>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>{r.dispositionName}</td>
                  <td className="px-4 py-3">
                    <Toggle
                      checked={r.status}
                      onChange={() => handleStatusToggle(r.id)}
                      ariaLabel={`Toggle status for ${r.dispositionName}`}
                    />
                  </td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>{r.createdBy}</td>
                  <td className={`px-4 py-3 text-sm whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-700'}`}>
                    {formatDateTime(r.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEditModal(r)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-blue-800 bg-blue-900/20 text-blue-400 hover:bg-blue-900/40' : 'border-blue-100 bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                        title="Edit"
                        aria-label={`Edit ${r.dispositionName}`}
                      >
                        <img
                          src={editIcon}
                          alt="Edit"
                          className="w-5 h-5"
                          aria-hidden="true"
                          style={{ filter: isDark ? 'invert(1) brightness(2)' : 'none', opacity: 0.8 }}
                        />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(r.id, r.dispositionName)}
                        className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${isDark ? 'border-red-800 bg-red-900/20 text-red-400 hover:bg-red-900/40' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'}`}
                        title="Delete"
                        aria-label={`Delete ${r.dispositionName}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}